/**
 * AGM 4종 스튜디오 — 기존 Agm*Form.vue 의 비-UI 로직을 그대로 옮긴 것(폼 컴포넌트는 그대로 둠).
 *   · 엔티티 이사/회원/(감사인)/프로필 로드 → 이사 수·자동 서명자·의장·회원 유형(복수/단독 법인/단독 개인)·퇴임 이사
 *   · 이사 수/지정 서명자/퇴임 이사 토글 watcher
 *   · ✨ 선택 반영(chairman / auditor / member·soleMember·memberRepresentative / retiringDirector / signer)
 */
import { ref, watch, type ComputedRef } from 'vue';
import type { OfficerDetail } from '@/features/AI-documents/types/docGen.types';
import { docGenApi } from '@/features/AI-documents/api/docGenApi';
import { getActiveAuditors, getActiveDirectors, getActiveMembers } from '../../sgDocGenEntityApi';
import { mapOfficerFromApi } from '../agm-ufs/composables/agmUfsAttendanceSigners';
import { mapAuditorsFromApi } from '../agm-exempted-afs/composables/agmExemptedAfsAuditor';
import { applyAgmMemberPersonSelection, resolveAgmMemberDisplayName } from '../agmMemberPersonLookup';
import { syncRetiringDirectorsRows } from '../agmRetiringDirectorsSync';
import {
  resolveDirectorCountOnEntityLoad,
  shouldSyncAutoSignersOnDirectorCountChange,
  shouldSyncAutoSignersOnEntityLoad,
} from '../agmResolutionSignersSync';

export interface AgmStudioEntityLogicOptions {
  model: Record<string, any>;
  entityCd: ComputedRef<string>;
  editMode: ComputedRef<boolean>;
  applyProfile: (model: any, payload: Record<string, any>) => void;
  /** AFS·Exempted AFS — 감사인 로드 + 비어 있으면 첫 감사인 */
  withAuditor?: boolean;
  /** AFS·UFS — 의장 비어 있으면 첫 이사 */
  withChairman?: boolean;
  selectedPerson: () => OfficerDetail | null | undefined;
  activePersonContext: () => Record<string, any> | null | undefined;
  tag?: string;
}

export function useAgmStudioEntityLogic(o: AgmStudioEntityLogicOptions) {
  const m = o.model;
  const loading = ref(false);
  /** 확인 게이트용 — 엔티티 주주 기록으로 판별한 회원 구성 + 주주 요약(Final Dividends 게이트와 같은 방식) */
  const detectedMemberType = ref<'' | 'multiple' | 'sole_corporate' | 'sole_individual'>('');
  const memberSummary = ref<string[]>([]);
  const loaded = ref(false);
  const directorsFromEntity = ref<Array<{ name: string }>>([]);
  const auditorsFromEntity = ref<string[]>([]);
  const trim = (v: unknown) => (v == null ? '' : String(v).trim());

  function buildAutoSignerRows(): Array<{ name: string }> {
    const source = directorsFromEntity.value.map((d) => trim(d.name)).filter(Boolean);
    if (!source.length) {
      const n = Number.isFinite(m.directorCount) ? Math.max(0, Number(m.directorCount)) : 0;
      return Array.from({ length: n }).map((_, i) => ({ name: `Director ${i + 1}` }));
    }
    return source.map((name) => ({ name }));
  }
  function syncAutoSignersIfNeeded() {
    if (m.specifySigners) return;
    m.signers = buildAutoSignerRows();
  }
  function syncRetiringDirectorsFromEntity() {
    m.retiringDirectors = syncRetiringDirectorsRows({
      editMode: o.editMode.value,
      retiringDirectors: m.retiringDirectors,
      entityDirectors: m.entityDirectors || [],
      directorsFromEntity: directorsFromEntity.value,
    });
  }
  function applyDefaultAuditor() {
    if (trim(m.auditorName)) return;
    const first = auditorsFromEntity.value.find((n) => trim(n));
    if (first) m.auditorName = first;
  }

  /** 회사를 바꾼 경우 이전 회사에서 채운 값(감사인·의장·단독 회원/대표자·회원 목록·퇴임 이사)을 비운다 — 안 그러면 "비어 있을 때만 채움" 규칙 때문에 이전 회사 값이 남는다. */
  let lastEntityCd = '';
  function clearEntityDerived() {
    if ('auditorName' in m) m.auditorName = '';
    if ('chairmanName' in m) m.chairmanName = '';
    m.memberName = '';
    m.memberRepresentativeName = '';
    m.members = [];
    m.retiringDirectors = [];
    m.entityDirectors = [];
    m.entityMembers = [];
    directorsFromEntity.value = [];
    auditorsFromEntity.value = [];
  }
  function resetGate() {
    detectedMemberType.value = '';
    memberSummary.value = [];
    loaded.value = false;
  }

  async function loadEntityData(entityCd: string) {
    if (!entityCd || loading.value) return;
    if (lastEntityCd && lastEntityCd !== entityCd) { clearEntityDerived(); resetGate(); }
    lastEntityCd = entityCd;
    loading.value = true;
    try {
      const [dirRes, memRes, audRes, profileRes] = await Promise.all([
        getActiveDirectors(entityCd),
        getActiveMembers(entityCd),
        o.withAuditor ? getActiveAuditors(entityCd).catch(() => null) : Promise.resolve(null),
        docGenApi.getEntityProfile(entityCd, 'SG').catch(() => null),
      ]);
      const profilePayload = (profileRes as { data?: Record<string, any> } | null)?.data ?? profileRes;
      if (profilePayload) o.applyProfile(m, profilePayload as Record<string, any>);

      if (o.withAuditor) {
        auditorsFromEntity.value = mapAuditorsFromApi((audRes as any)?.data?.auditors || []);
        applyDefaultAuditor();
      }

      const directors: any[] = (dirRes as any)?.data?.directors || [];
      m.entityDirectors = directors.map((d: any) => mapOfficerFromApi(d, 'director')).filter((d: any) => d.name);
      directorsFromEntity.value = m.entityDirectors.map((d: any) => ({ name: d.name }));
      m.directorCount = resolveDirectorCountOnEntityLoad({
        editMode: o.editMode.value, savedDirectorCount: m.directorCount, entityDirectorCount: directors.length || 1,
      });
      if (shouldSyncAutoSignersOnEntityLoad({ editMode: o.editMode.value, specifySigners: m.specifySigners, signers: m.signers })) {
        syncAutoSignersIfNeeded();
      }
      if (o.withChairman && directors.length > 0 && !m.chairmanName) m.chairmanName = directors[0].name || '';

      const members: any[] = (memRes as any)?.data?.members || [];
      m.entityMembers = members.map((x: any) => mapOfficerFromApi(x, 'shareholder')).filter((x: any) => x.name);
      const nameOf = (x: any) => x.name || x.contactName || `${x.firstName || ''} ${x.surname || x.lastName || ''}`.trim();
      const isCorp = (x: any) => {
        const cat = trim(x.categoryCd || x.CATEGORY_CD).toUpperCase();
        const byFallback = x.entityType === 'COMPANY' || x.idType === 'UEN' || x.type === 'corporate';
        return cat === 'B09001' || (cat !== 'B09002' && byFallback);
      };
      memberSummary.value = members.map((x: any) => `${nameOf(x)} (${isCorp(x) ? 'Corporate' : 'Individual'})`).filter((s: string) => !s.startsWith(' ('));
      detectedMemberType.value = members.length > 1 ? 'multiple' : members.length === 1 ? (isCorp(members[0]) ? 'sole_corporate' : 'sole_individual') : '';
      if (!o.editMode.value) {
        if (members.length === 1) {
          const x = members[0];
          m.memberType = isCorp(x) ? 'sole_corporate' : 'sole_individual';
          m.memberName = nameOf(x);
        } else if (members.length > 1) {
          m.memberType = 'multiple';
          m.members = members.map((x: any) => ({
            name: nameOf(x), address: x.primaryAddress || x.address || x.residentialAddress || '', contactCd: x.contactCd || '',
          }));
        }
      }
      if (m.hasRetiringDirectors) syncRetiringDirectorsFromEntity();
    } catch (err) {
      console.warn(`[${o.tag || 'AgmStudio'}] loadEntityData failed:`, err);
    } finally {
      loading.value = false;
      loaded.value = true;
    }
  }

  // 회사 연결을 해제하면(No linked entity) 확인 카드의 이전 회사 요약도 지운다
  watch(o.entityCd, (ec) => { if (ec) void loadEntityData(ec); else resetGate(); }, { immediate: true });

  watch(() => m.directorCount, (count) => {
    if (count < 3) m.specifySigners = false;
    if (shouldSyncAutoSignersOnDirectorCountChange({ editMode: o.editMode.value, specifySigners: m.specifySigners, signers: m.signers })) {
      syncAutoSignersIfNeeded();
    }
  });
  watch(() => m.hasRetiringDirectors, (on) => { if (on) syncRetiringDirectorsFromEntity(); });
  if (o.withAuditor) watch(() => m.hasDirectorsRemuneration, (on) => { if (on) applyDefaultAuditor(); });
  watch(() => m.specifySigners, (on) => {
    if (!on) { syncAutoSignersIfNeeded(); return; }
    if (!m.signers.length) m.signers = buildAutoSignerRows();
  });

  watch(o.selectedPerson, (person) => {
    const ctx = o.activePersonContext();
    if (!person || !ctx) return;
    const name = resolveAgmMemberDisplayName(person);
    if (!name) return;
    if (ctx.fieldGroup === 'chairman') m.chairmanName = name;
    else if (ctx.fieldGroup === 'auditor') m.auditorName = name;
    else if (applyAgmMemberPersonSelection(m as any, ctx, person)) { /* member / soleMember / representative */ }
    else if (ctx.fieldGroup === 'retiringDirector' && typeof ctx.index === 'number') {
      if (m.retiringDirectors[ctx.index]) m.retiringDirectors[ctx.index].name = name;
    } else if (ctx.fieldGroup === 'signer' && typeof ctx.index === 'number') {
      if (m.signers[ctx.index]) m.signers[ctx.index].name = name;
    }
  });

  return { loading, loaded, loadEntityData, detectedMemberType, memberSummary };
}
